import i18next from 'i18next';

/**
 * Dates and times for every CGuard SPA, in the viewer's language and the
 * browser's time zone. Times always carry the short zone name ("GMT+4") so a
 * reading is never ambiguous between local and UTC. Invalid input renders
 * as an em dash rather than "Invalid Date".
 */

const INVALID = '—';

function activeLocale(): string {
  return i18next.resolvedLanguage ?? i18next.language ?? 'en';
}

function parse(value: string | number | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * "Oct 24, 2026". Pass `{ utc: true }` for calendar dates stored as midnight
 * UTC (contract starts, expiries) so every reader sees the same day.
 */
export function formatDate(
  value: string | number | Date,
  options: { readonly utc?: boolean } = {},
): string {
  const date = parse(value);
  if (!date) return INVALID;
  return new Intl.DateTimeFormat(activeLocale(), {
    dateStyle: 'medium',
    ...(options.utc ? { timeZone: 'UTC' } : {}),
  }).format(date);
}

/**
 * "Oct 24, 2026, 5:36 AM GMT+4"; pass `{ seconds: true }` for evidence
 * timestamps that need "5:36:07 AM".
 */
export function formatDateTime(
  value: string | number | Date,
  options: { readonly seconds?: boolean } = {},
): string {
  const date = parse(value);
  if (!date) return INVALID;
  return dateTimeFormat(options).format(date);
}

/**
 * A from–to window that names the shared date once:
 * "Oct 24, 2026, 5:36:07 – 5:38:07 AM GMT+4". Same options as `formatDateTime`.
 */
export function formatDateTimeRange(
  from: string | number | Date,
  to: string | number | Date,
  options: { readonly seconds?: boolean } = {},
): string {
  const start = parse(from);
  const end = parse(to);
  if (!start || !end) return INVALID;
  const format = dateTimeFormat(options);
  return start <= end
    ? format.formatRange(start, end)
    : `${format.format(start)} – ${format.format(end)}`;
}

function dateTimeFormat(options: { readonly seconds?: boolean }): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(activeLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(options.seconds ? { second: '2-digit' } : {}),
    timeZoneName: 'short',
  });
}

/** "5:36 AM GMT+4"; pass `{ seconds: true }` for "5:36:07 AM GMT+4". */
export function formatTime(
  value: string | number | Date,
  options: { readonly seconds?: boolean } = {},
): string {
  const date = parse(value);
  if (!date) return INVALID;
  return new Intl.DateTimeFormat(activeLocale(), {
    hour: 'numeric',
    minute: '2-digit',
    ...(options.seconds ? { second: '2-digit' } : {}),
    timeZoneName: 'short',
  }).format(date);
}

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "4 minutes ago" / "in 2 hours", from the common `time.*` ICU plurals so the
 * wording and Arabic plural forms come from the catalog. Anything within five
 * seconds of `now` reads as "now".
 */
export function formatRelative(value: string | number | Date, now: Date = new Date()): string {
  const date = parse(value);
  if (!date) return INVALID;
  const deltaSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(deltaSeconds);
  if (abs < 5) return i18next.t('common:time.now');
  const past = deltaSeconds < 0;
  const [unit, count] =
    abs < MINUTE
      ? (['Seconds', abs] as const)
      : abs < HOUR
        ? (['Minutes', Math.floor(abs / MINUTE)] as const)
        : abs < DAY
          ? (['Hours', Math.floor(abs / HOUR)] as const)
          : (['Days', Math.floor(abs / DAY)] as const);
  const key = past ? `${unit.toLowerCase()}Ago` : `in${unit}`;
  return i18next.t(`common:time.${key}`, { count });
}
